import {
  getStoredRefreshToken,
  getStoredToken,
  isExpiredTokenError,
  readApiError,
  useAuthStore,
} from '@/shared/platform';
import axios, { type AxiosError, type AxiosInstance, type InternalAxiosRequestConfig } from 'axios';

/**
 * The client for RCM's own backend.
 *
 * Deliberately not `getMainApi()`. That instance is core-api-az: it carries
 * `X-Location-Id` as the practice-management `ascend_id`, and the shared
 * platform code uses it for things like `/organization/api/locations/`, which
 * the sidebar's location switcher needs. Pointing it at the RCM backend would
 * send those calls to a server that has never heard of them.
 *
 * So RCM gets its own instance. It shares the session — same stored token,
 * same refresh — and nothing else.
 */

/**
 * Empty means same-origin, which is the normal case: Vercel rewrites
 * `/api/rcm/*` to the RCM backend, so the browser makes a same-origin request
 * and there is no CORS preflight and no mixed content to worry about. Set it
 * to an absolute URL to talk to a backend directly.
 */
// `import.meta.env` is untyped here (the app declares no ImportMetaEnv),
// so narrow it once rather than reading an `any` at each use.
const env = import.meta.env as Record<string, string | undefined>;
const BASE_URL = env.VITE_RCM_API_BASE_URL ?? '';

type RetriableConfig = InternalAxiosRequestConfig & { _retry?: boolean };

let instance: AxiosInstance | null = null;

function create(): AxiosInstance {
  const api = axios.create({
    baseURL: BASE_URL,
    // Bearer only. The backend sets CORS_ALLOW_CREDENTIALS off, and sending
    // cookies cross-origin would be refused anyway.
    withCredentials: false,
    headers: { 'Content-Type': 'application/json' },
  });

  api.interceptors.request.use((config) => {
    const token = getStoredToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });

  api.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const original = error.config as RetriableConfig | undefined;
      const apiError = readApiError(error);
      const shouldRefresh =
        isExpiredTokenError(apiError) ||
        (apiError?.code === 'not_authenticated' && getStoredRefreshToken() !== null);

      if (original && shouldRefresh && !original._retry) {
        original._retry = true;
        try {
          const token = await useAuthStore.getState().refreshSession();
          if (token) {
            original.headers.Authorization = `Bearer ${token}`;
            return await api.request(original);
          }
        } catch {
          // Fall through and reject: see below for why we do not sign out.
        }
      }

      // A 401 from RCM never signs the user out. The account app owns the
      // session; an RCM outage or a misconfigured base URL must not look like
      // a login failure and throw away a perfectly good account session.
      return await Promise.reject(error);
    }
  );

  return api;
}

export function getRcmApi(): AxiosInstance {
  instance ??= create();
  return instance;
}

/** For tests, and for a sign-out that should not leave a token behind. */
export function resetRcmApi(): void {
  instance = null;
}
